import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { Button, Checkbox } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';

import type { DealStage } from '../api/deals.client.js';
import type { KanbanCardModel } from '../lib/kanban.js';

const NEXT_STAGE: Partial<Record<DealStage, DealStage>> = {
  New: 'Contacted',
  Contacted: 'Negotiation',
  Negotiation: 'Won',
};

const STAGE_ACCENT: Record<DealStage, string> = {
  New: '#2563eb',
  Contacted: '#d97706',
  Negotiation: '#eab308',
  Won: '#16a34a',
  Lost: '#dc2626',
};

export interface DealCardProps {
  card: KanbanCardModel;
  isSelected: boolean;
  isUpdating: boolean;
  onMoveForward: (dealId: string, toStage: DealStage) => void;
  onToggleSelected: (dealId: string) => void;
  onDragStart: (dealId: string) => void;
}

export function DealCard({ card, isSelected, isUpdating, onMoveForward, onToggleSelected, onDragStart }: DealCardProps): ReactElement {
  const nextStage = NEXT_STAGE[card.stage];
  const nextStepLabel = card.nextActivityDue !== null ? 'Следующий шаг' : null;

  return (
    <article
      draggable
      data-testid={`deal-card-${card.dealId}`}
      className="cursor-grab active:cursor-grabbing transition-transform hover:-translate-y-0.5 relative group text-sm"
      onDragStart={() => onDragStart(card.dealId)}
      style={{
        padding: '12px 14px',
        backgroundColor: '#fffdfa',
        borderRadius: 8,
        border: '1px solid #e7e5e4',
        borderLeft: `3px solid ${STAGE_ACCENT[card.stage]}`,
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.02)'
      }}
    >
      <Checkbox
        checked={isSelected}
        aria-label={`Выбрать сделку ${card.title}`}
        onChange={() => onToggleSelected(card.dealId)}
        onClick={(event) => event.stopPropagation()}
        className="absolute"
        style={{ right: 12, top: 12 }}
      />
      
      <div className="flex items-center justify-between" style={{ marginBottom: 8 }}>
        <span
          className="rounded font-medium"
          style={{ padding: '2px 6px', fontSize: 10, backgroundColor: '#f5f5f4', color: '#78716c' }}
        >
          {card.contactName}
        </span>
      </div>

      <h4 className="font-semibold pr-6" style={{ fontSize: 13, color: '#44403c', lineHeight: 1.3, marginBottom: 8, wordBreak: 'break-word' }}>
        <Link
          className="hover:underline"
          to={`/deals/${card.dealId}`}
          style={{ color: 'inherit' }}
        >
          {card.title}
        </Link>
      </h4>

      <div className="flex items-center justify-between" style={{ marginTop: 10 }}>
        <div className="flex items-center gap-2">
          <span style={{ fontSize: 11, fontWeight: 600, color: '#a8a29e', fontFamily: 'monospace' }}>
            {card.amountLabel}
          </span>
          {card.tags.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              <span className="rounded-full flex items-center justify-center font-medium" style={{ padding: '1px 6px', fontSize: 9, backgroundColor: '#fef3c7', color: '#d97706' }}>
                {card.tags[0]}
              </span>
            </div>
          ) : null}
        </div>
        
        {nextStepLabel !== null ? (
          <span className="rounded-full font-bold" title="Запланирована активность" style={{ padding: '2px 6px', backgroundColor: '#d97706', color: '#fff', fontSize: 10 }}>
            {nextStepLabel}
          </span>
        ) : null}
      </div>

      {nextStage !== undefined ? (
        <Button
          size="small"
          disabled={isUpdating}
          onClick={(e) => {
             e.stopPropagation();
             onMoveForward(card.dealId, nextStage);
          }}
          style={{ marginTop: 12, fontSize: 11, width: '100%', fontWeight: 500 }}
          icon={<ArrowRightOutlined />}
        >
          {nextStage}
        </Button>
      ) : null}
    </article>
  );
}
